import React from "react";
import PageWrapper from "../components/PageWrapper";
import "./css/Contact.css";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | GTAFanPage.com</title>
        <meta
          name="description"
          content="Read the Terms of Service for GTAFanPage.com. Learn about user responsibilities, content usage, shared services, and platform policies."
        />
        <link
          rel="canonical"
          href="https://www.gtafanpage.com/terms-and-conditions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Service | GTAFanPage.com" />
        <meta
          property="og:description"
          content="Understand the rules, responsibilities, and policies that govern the use of GTAFanPage.com."
        />
        <meta
          property="og:url"
          content="https://www.gtafanpage.com/terms-and-conditions"
        />
        <meta
          property="og:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Terms of Service | GTAFanPage.com"
        />
        <meta
          name="twitter:description"
          content="Review the legal terms that govern your use of GTAFanPage.com."
        />
        <meta
          name="twitter:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Terms of Service",
            url: "https://www.gtafanpage.com/terms-and-conditions",
            description:
              "Terms of Service governing the use of GTAFanPage.com, including user responsibilities and shared platform services.",
            publisher: {
              "@type": "Organization",
              name: "GTAFanPage",
              url: "https://www.gtafanpage.com",
              logo: {
                "@type": "ImageObject",
                url: "https://www.gtafanpage.com/logo192.png",
              },
            },
          })}
        </script>
      </Helmet>

      <PageWrapper>
        <div className="contact-page-wrapper">
          <div className="contact-card about-card">
            <h1>Terms of Service</h1>
            <p className="about-subtitle">
              Last updated: <strong>January 2026</strong>
            </p>

            {/* 1 */}
            <section className="about-section">
              <h3>1. Acceptance of Terms</h3>
              <p>
                By accessing or using GTAFanPage.com, you agree to be bound by
                these Terms of Service, our Privacy Policy, and any additional
                guidelines posted on the website. If you do not agree with these
                terms, please discontinue use of the site.
              </p>
              <p>
                You must be at least <strong>13 years of age</strong> to use this
                website.
              </p>
            </section>

            {/* 2 */}
            <section className="about-section">
              <h3>2. Nature of the Website</h3>
              <p>
                GTAFanPage.com is an independent, fan-operated digital media and
                community platform dedicated to coverage of the{" "}
                <strong>Grand Theft Auto</strong> video game franchise.
              </p>
              <p>
                This website is <strong>not affiliated with, endorsed by, or
                  sponsored by</strong> Rockstar Games, Take-Two Interactive, or
                any of their subsidiaries.
              </p>
              <p>
                All content is provided for informational, editorial, and
                entertainment purposes only.
              </p>
            </section>

            {/* 3 */}
            <section className="about-section">
              <h3>3. User Responsibilities</h3>
              <p>By using GTAFanPage.com, you agree not to:</p>
              <ul className="about-list">
                <li>Engage in illegal, abusive, or harmful activities</li>
                <li>Harass, impersonate, or threaten other users</li>
                <li>Upload malicious software or spam</li>
                <li>Attempt to gain unauthorized access to site systems</li>
                <li>Misuse community features or reporting tools</li>
              </ul>
              <p>
                We reserve the right to restrict, suspend, or terminate access
                for violations of these terms.
              </p>
            </section>

            {/* 4 */}
            <section className="about-section">
              <h3>4. User-Generated Content</h3>
              <p>
                Users may submit content such as missions, comments, posts, media, or other
                contributions where applicable.
              </p>
              <p>
                By submitting content, you confirm that you own the rights to
                the content or have permission to share it, and that it does not
                violate applicable laws or third-party rights.
              </p>
              <p>
                GTAFanPage.com reserves the right to moderate, edit, or remove
                user-generated content at its discretion.
              </p>
            </section>

            {/* 5 */}
            <section className="about-section">
              <h3>5. Intellectual Property</h3>
              <p>
                All original content published on GTAFanPage.com, including
                articles, design, layout, and branding, is the property of
                GTAFanPage.com unless otherwise stated.
              </p>
              <p>
                All trademarks, logos, characters, and game-related media are
                the property of their respective owners and are used under fair
                use for editorial and informational purposes.
              </p>
            </section>

            {/* 6 */}
            <section className="about-section">
              <h3>6. Accuracy & Availability</h3>
              <p>
                While we strive to provide accurate and up-to-date information,
                GTAFanPage.com makes no guarantees regarding the completeness,
                reliability, or accuracy of content.
              </p>
              <p>
                Content may change without notice and may include opinions,
                speculation, or community-generated material clearly identified
                as such.
              </p>
            </section>

            {/* 7 */}
            <section className="about-section">
              <h3>7. Third-Party Links</h3>
              <p>
                GTAFanPage.com may include links to third-party websites or
                services. We are not responsible for the content, policies, or
                practices of external sites.
              </p>
            </section>

            {/* 8 */}
            <section className="about-section">
              <h3>8. Limitation of Liability</h3>
              <p>
                To the fullest extent permitted by law, GTAFanPage.com shall not
                be liable for any direct, indirect, incidental, or consequential
                damages arising from your use of the website.
              </p>
              <p>
                The website is provided on an <strong>“as is”</strong> and{" "}
                <strong>“as available”</strong> basis.
              </p>
            </section>

            {/* 9 */}
            <section className="about-section">
              <h3>9. Shared Services & Network Platforms</h3>
              <p>
                GTAFanPage.com operates as part of an independent fan media
                network. Certain features, including user accounts,
                authentication, and community functionality, may be shared
                across affiliated platforms.
              </p>
              <p>
                Information provided by users on GTAFanPage.com—such as account
                credentials, profile details, and community contributions—may
                be accessible and used on affiliated platforms within this
                network, including <strong>RDRFan.com</strong>, for purposes
                such as authentication, account continuity, and community
                participation.
              </p>
              <p>
                This shared use of information is limited to internal platform
                operations and is not intended for commercial data resale or
                third-party distribution.
              </p>
            </section>

            {/* 10 */}
            <section className="about-section">
              <h3>10. Changes to These Terms</h3>
              <p>
                We reserve the right to update or modify these Terms of Service
                at any time. Changes will be posted on this page, and continued
                use of the website constitutes acceptance of the updated terms.
              </p>
            </section>

            {/* 11 */}
            <section className="about-section">
              <h3>11. Contact Information</h3>
              <p>
                If you have questions, concerns, or legal inquiries regarding
                these Terms of Service, please visit our{" "}
                <Link to="/contact" className="contact-link">
                  Contact page
                </Link>.
              </p>
            </section>

            <footer className="about-footer">
              <p className="about-tagline">
                <strong>GTAFanPage.com</strong> — Independent coverage, trusted
                fan insight, and community-driven content for the Grand Theft
                Auto universe.
              </p>
            </footer>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default Terms;
